import React, { FunctionComponent, ReactNode } from 'react';
import { withRouter, RouteComponentProps } from 'react-router';
import styled, { StyledComponent } from 'styled-components';
import { Theme } from '@inplayer-org/inplayer-ui';

// utils
import media from 'utils/mediaQueries';

// components
import Loader from 'components/Loader';
import BoxItemAdder from 'components/BoxItemAdder';
import BoxItem, { Item } from 'components/BoxItem';
import BoxItemDefaultGrid from 'components/BoxItemDefaultGrid';
import Restricted from 'restrictions/roles/components/Restricted';

const ItemsContainer: StyledComponent<'div', Theme> = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 90%;
  margin: 0 auto 2rem;
  padding-top: 2rem;
  position: relative;
`;

const GridWithMediaQueries: StyledComponent<'div', Theme> = styled(BoxItemDefaultGrid)`
  ${media.customMaxWidth(1200)} {
    grid-template-columns: repeat(5, minmax(18%, 1fr));
  }

  ${media.customMaxWidth(1000)} {
    grid-template-columns: repeat(4, minmax(23%, 1fr));
  }

  ${media.customMaxWidth(760)} {
    grid-template-columns: repeat(3, minmax(31%, 1fr));
  }

  ${media.customMaxWidth(600)} {
    grid-template-columns: repeat(2, minmax(48%, 1fr));
  }
`;

export interface BoxAdderParams {
  itemTitle: string;
  handleAddNew?: (event: any) => void;
}

interface Props extends RouteComponentProps {
  itemTitle: string;
  items: any;
  boxItemRender: (item: Item) => ReactNode;
  handleAddNew: (event: any) => void;
  children?: ReactNode;
  renderCustomBoxAdder?: (params: BoxAdderParams) => ReactNode;
  customItemGrid?: StyledComponent<'div', Theme>;
  isReadOnly?: boolean;
}

const BoxContainer: FunctionComponent<Props> = ({
  itemTitle,
  items,
  boxItemRender,
  handleAddNew,
  children,
  customItemGrid,
  renderCustomBoxAdder,
  isReadOnly = false,
}) => {
  const renderBoxItem = ({ image, item }: { image: string; item: Item }) => (
    <BoxItem key={item.id} boxItem={item} backgroundImgSrc={image} render={boxItemRender} />
  );

  const ItemGrid = customItemGrid || GridWithMediaQueries;

  if (items.isFetching) {
    return <Loader />;
  }

  return (
    <ItemsContainer>
      {children}
      <ItemGrid>
        <Restricted isRestricted={isReadOnly}>
          {renderCustomBoxAdder ? (
            renderCustomBoxAdder({ itemTitle, handleAddNew })
          ) : (
            <BoxItemAdder itemTitle={itemTitle} handleAddNew={handleAddNew} />
          )}
        </Restricted>
        {items.map(renderBoxItem)}
      </ItemGrid>
    </ItemsContainer>
  );
};

export default withRouter(BoxContainer);
